import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { FileIcon, UploadIcon, XIcon } from "../lib/icons";

export interface FileDropProps {
  accept?: string;
  multiple?: boolean;
  /** Taille maximale par fichier, en octets. Les fichiers trop lourds sont refusés. */
  maxSize?: number;
  onFilesChange?: (files: File[]) => void;
  disabled?: boolean;
  hint?: ReactNode;
  className?: string;
}

/** 1536 → « 1,5 ko ». */
function formatSize(bytes: number): string {
  const units = ["o", "ko", "Mo", "Go"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0).replace(".", ",")} ${units[unit]}`;
}

/**
 * Zone de dépôt de fichiers.
 *
 * Le glisser-déposer est un CONFORT, jamais le seul chemin : la zone entière
 * est un `<label>` relié à un `<input type="file">` masqué, donc atteignable au
 * clavier et parfaitement annoncée. Une zone qui ne réagit qu'au glisser exclut
 * tout utilisateur au clavier — c'est l'erreur classique du composant maison.
 *
 * Le compteur `dragDepth` gère un piège réel : `dragleave` se déclenche aussi
 * en passant d'un enfant à un autre à l'intérieur de la zone. Sans compteur,
 * la surbrillance clignote pendant tout le survol.
 */
export function FileDrop({
  accept,
  multiple = false,
  maxSize,
  onFilesChange,
  disabled,
  hint,
  className,
}: FileDropProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const accept_ = (incoming: FileList | null) => {
    if (!incoming) return;

    const list = Array.from(incoming);
    const tooBig = maxSize ? list.filter((file) => file.size > maxSize) : [];

    if (tooBig.length > 0) {
      setError(`Fichier trop volumineux (maximum ${formatSize(maxSize ?? 0)}).`);
      return;
    }

    setError(null);
    const next = multiple ? [...files, ...list] : list.slice(0, 1);
    setFiles(next);
    onFilesChange?.(next);
  };

  const remove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    onFilesChange?.(next);
    // Sans ce reset, redéposer le même fichier ne déclenche aucun `change`.
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (!disabled) accept_(event.dataTransfer.files);
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <label
        htmlFor={inputId}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragLeave={() => {
          dragDepth.current -= 1;
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center",
          "transform-gpu will-change-transform transition-[background-color,border-color,transform] duration-normal ease-standard",
          dragging
            ? // La zone se dilate très légèrement : elle « attrape » le fichier.
              "scale-[1.01] border-accent bg-accent-subtle"
            : "border-border bg-surface-sunken hover:border-border-strong hover:bg-surface-raised",
          disabled && "pointer-events-none opacity-50",
          "has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-ring",
        )}
      >
        <span
          className={cn(
            "flex size-9 items-center justify-center rounded-full transition-colors duration-normal",
            dragging ? "bg-accent text-fg-on-accent" : "bg-surface-raised text-fg-muted",
          )}
        >
          <UploadIcon className="size-4" />
        </span>

        <span className="text-sm text-fg">
          <span className="font-medium text-accent-fg">Choisir un fichier</span> ou le glisser ici
        </span>
        {hint && <span className="text-xs text-fg-muted">{hint}</span>}

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(event) => accept_(event.target.files)}
          className="sr-only"
        />
      </label>

      {error && (
        <p role="alert" className="text-xs text-danger-fg">
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex transform-gpu animate-slide-up items-center gap-3 rounded-md border border-border bg-surface-raised px-3 py-2"
            >
              <FileIcon className="size-4 shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">{file.name}</span>
              <span className="shrink-0 font-mono text-[11px] text-fg-muted">
                {formatSize(file.size)}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Retirer ${file.name}`}
                className="rounded-sm p-1 text-fg-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-danger focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <XIcon className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
